import { ECHION_BASE_STATS } from "../data/echionStats";

const STAT_KEYS = [
  "hp",
  "attackPower",
  "defense",
  "skillAmp",
  "maxSp",
  "attackSpeedPercent",
  "cooldownReduction",
  "cooldownReductionPercent",
  "moveSpeedPercent",
  "moveSpeedFlat",
  "armorPenetration",
  "armorPenetrationPercent",
  "skillAmpPenetration",
  "skillAmpPenetrationPercent",
  "critChancePercent",
  "critDamagePercent",
  "omniVampPercent",
  "basicAttackRange",
  "crowdControlResistance",
  "crowdControlResistancePercent",
  "basicAttackAmpPercent",
  "basicAttackAmpPerLevelMin",
  "basicAttackAmpPerLevelMax",
];

function clampLevel(level) {
  return Math.min(20, Math.max(1, Number(level) || 1));
}

function roundStat(value) {
  return Math.round((Number(value) || 0) * 10) / 10;
}

function calculateLevelRangeValue(minValue, maxValue, level) {
  const min = Number(minValue) || 0;
  const max = Number(maxValue) || 0;

  if (!min && !max) {
    return 0;
  }

  return min + ((max - min) * (clampLevel(level) - 1)) / 19;
}

function calculateBaseStats(level) {
  const normalizedLevel = clampLevel(level);
  const levelOffset = normalizedLevel - 1;
  const level1Stats = ECHION_BASE_STATS.baseStats.level1;
  const growth = ECHION_BASE_STATS.growthPerLevel;

  return STAT_KEYS.reduce((stats, key) => ({
    ...stats,
    [key]: roundStat((level1Stats[key] || 0) + (growth[key] || 0) * levelOffset),
  }), {});
}

function addDerivedLevelStats(bonus, itemStats, level) {
  const normalizedLevel = clampLevel(level);

  bonus.hp = roundStat(
    (bonus.hp || 0)
    + (Number(itemStats.maxHpPerLevel) || 0) * normalizedLevel
    + calculateLevelRangeValue(itemStats.maxHpPerLevelMin, itemStats.maxHpPerLevelMax, normalizedLevel)
  );
  bonus.attackPower = roundStat(
    (bonus.attackPower || 0)
    + (Number(itemStats.attackPowerPerLevel) || 0) * normalizedLevel
    + calculateLevelRangeValue(itemStats.attackPowerPerLevelMin, itemStats.attackPowerPerLevelMax, normalizedLevel)
  );
  bonus.defense = roundStat(
    (bonus.defense || 0)
    + (Number(itemStats.defensePerLevel) || 0) * normalizedLevel
  );
  bonus.skillAmp = roundStat(
    (bonus.skillAmp || 0)
    + (Number(itemStats.skillAmpPerLevel) || 0) * normalizedLevel
    + calculateLevelRangeValue(itemStats.skillAmpPerLevelMin, itemStats.skillAmpPerLevelMax, normalizedLevel)
  );
  bonus.basicAttackAmpPercent = roundStat(
    (bonus.basicAttackAmpPercent || 0)
    + (Number(itemStats.basicAttackAmpPerLevel) || 0) * normalizedLevel
    + calculateLevelRangeValue(itemStats.basicAttackAmpPerLevelMin, itemStats.basicAttackAmpPerLevelMax, normalizedLevel)
  );
}

function calculateEquipmentBonus(equipment, level) {
  const emptyBonus = STAT_KEYS.reduce((bonus, key) => ({
    ...bonus,
    [key]: 0,
  }), {});

  return Object.values(equipment || {}).reduce((bonus, item) => {
    const itemStats = item?.stats || {};

    STAT_KEYS.forEach((key) => {
      const statValue = key === "hp"
        ? itemStats.hp || itemStats.maxHp || 0
        : itemStats[key] || 0;

      bonus[key] = roundStat((bonus[key] || 0) + statValue);
    });

    addDerivedLevelStats(bonus, itemStats, level);

    return bonus;
  }, emptyBonus);
}

function getItemSkillEntries(item) {
  const itemSkills = Array.isArray(item?.itemSkills)
    ? item.itemSkills.filter(Boolean)
    : [];

  if (itemSkills.length > 0) {
    return itemSkills;
  }

  return item?.itemSkill ? [item.itemSkill] : [];
}

function getHeavyAttackPowerBonus(equipment, equipmentBonus) {
  const bonusHp = Number(equipmentBonus?.hp) || 0;

  if (!bonusHp) {
    return 0;
  }

  return Object.values(equipment || {}).reduce((total, item) => {
    const heavySkill = getItemSkillEntries(item).find((skill) => (
      String(skill?.name || "").includes("묵직함")
      || String(skill?.description || "").includes("추가 체력의 2%만큼 공격력을 획득")
    ));

    if (!heavySkill) {
      return total;
    }

    const percentMatch = String(heavySkill.description || "").match(/추가\s*체력(?:의)?\s*(\d+(?:\.\d+)?)\s*%/);
    const percent = Number(percentMatch?.[1]) || 2;

    return total + bonusHp * percent / 100;
  }, 0);
}

function applyItemPassiveStatBonuses(equipment, equipmentBonus) {
  const heavyAttackPowerBonus = getStructuredHeavyAttackPowerBonus(equipment, equipmentBonus);

  if (heavyAttackPowerBonus) {
    equipmentBonus.attackPower = roundStat((equipmentBonus.attackPower || 0) + heavyAttackPowerBonus);
  }

  return equipmentBonus;
}

function getStructuredHeavyAttackPowerBonus(equipment, equipmentBonus) {
  const bonusHp = Number(equipmentBonus?.hp) || 0;
  const hasStructuredHeavyEffect = Object.values(equipment || {}).some((item) => (
    getItemSkillEntries(item).some((skill) => (
      Number(skill?.parameters?.bonusHpToAttackPowerRatio) > 0
    ))
  ));

  if (!hasStructuredHeavyEffect) {
    return getHeavyAttackPowerBonus(equipment, equipmentBonus);
  }

  return Object.values(equipment || {}).reduce((total, item) => {
    const heavySkill = getItemSkillEntries(item).find((skill) => (
      Number(skill?.parameters?.bonusHpToAttackPowerRatio) > 0
    ));
    const ratio = Number(heavySkill?.parameters?.bonusHpToAttackPowerRatio) || 0;

    return total + bonusHp * ratio;
  }, 0);
}

export function calculateStats({ level, equipment }) {
  const baseStats = calculateBaseStats(level);
  baseStats.critDamagePercent = baseStats.critDamagePercent || 175;
  const equipmentBonus = applyItemPassiveStatBonuses(
    equipment,
    calculateEquipmentBonus(equipment, level)
  );
  const finalStats = STAT_KEYS.reduce((stats, key) => ({
    ...stats,
    [key]: roundStat((baseStats[key] || 0) + (equipmentBonus[key] || 0)),
  }), {});
  const normalizedLevel = clampLevel(level);

  baseStats.basicAttackAmpPercent = normalizedLevel;
  finalStats.basicAttackAmpPercent = roundStat(
    normalizedLevel + (equipmentBonus.basicAttackAmpPercent || 0)
  );
  finalStats.maxHp = finalStats.hp;
  finalStats.bonusMaxHp = equipmentBonus.hp;
  finalStats.bonusAttackPower = equipmentBonus.attackPower;
  equipmentBonus.maxHp = equipmentBonus.hp;
  equipmentBonus.bonusMaxHp = equipmentBonus.hp;
  equipmentBonus.bonusAttackPower = equipmentBonus.attackPower;

  return {
    baseStats,
    equipmentBonus,
    finalStats,
  };
}
